import {
  IScheduleHandler,
  ScheduleContext,
  ScheduleEvaluationResult,
} from "../types/index";
import {
  Playlist,
  ScheduleType,
  HijriDateSchedule,
  isHijriDateSchedule,
  parseDaysString,
} from "../types";

/**
 * Hijri Date Schedule Handler
 *
 * Handles playlists with ScheduleType.HijriDate (5)
 * Active on specific Islamic calendar dates.
 *
 * Uses HijriDateComponents from PrayerTimes (already available from backend)
 * No external library needed.
 *
 * Examples:
 * - Ramadan (month 9, days "1-30")
 * - Eid al-Fitr (month 10, days "1-3")
 * - Eid al-Adha (month 12, days "10-13")
 * - Ashura (month 1, days "9-10")
 * - Laylat al-Qadr (month 9, days "21,23,25,27,29")
 */
export class HijriDateScheduleHandler implements IScheduleHandler {
  public readonly scheduleType = ScheduleType.HijriDate;

  /**
   * Evaluate if playlist is active based on Hijri date
   * Uses HijriDateComponents from context.prayerTimes
   */
  evaluate(
    playlist: Playlist,
    context: ScheduleContext
  ): ScheduleEvaluationResult {
    const schedule = playlist.schedule;

    if (!schedule || !isHijriDateSchedule(schedule)) {
      return {
        isActive: false,
        effectivePriority: playlist.priority,
        reason: "Invalid or missing HijriDate schedule configuration",
      };
    }

    // Check if prayer times with Hijri date are available
    if (!context.prayerTimes?.HijriDateComponents) {
      return {
        isActive: false,
        effectivePriority: playlist.priority,
        reason: "Hijri date information not available in prayer times",
      };
    }

    const hijriDate = context.prayerTimes.HijriDateComponents;
    const currentMonth = hijriDate.month; // 1-12
    const currentDay = hijriDate.day; // 1-30

    // Check if current Hijri month is in schedule
    if (!schedule.months.includes(currentMonth)) {
      return {
        isActive: false,
        effectivePriority: playlist.priority,
        reason: `Not active in Hijri month ${this.getHijriMonthName(
          currentMonth
        )}`,
      };
    }

    // Parse days string (e.g., "1-3", "10-13", "21,23,25,27,29")
    let activeDays: number[];
    try {
      activeDays = parseDaysString(schedule.days);
    } catch (error) {
      return {
        isActive: false,
        effectivePriority: playlist.priority,
        reason: `Invalid days configuration: ${schedule.days}`,
      };
    }

    // Check if current Hijri day is in schedule
    if (!activeDays.includes(currentDay)) {
      return {
        isActive: false,
        effectivePriority: playlist.priority,
        reason: `Not active on day ${currentDay} of ${this.getHijriMonthName(
          currentMonth
        )}`,
      };
    }

    // If time is specified, check if we're past that time
    if (schedule.time) {
      const currentTime = this.getTimeString(context.currentTime);
      if (currentTime < schedule.time) {
        return {
          isActive: false,
          effectivePriority: playlist.priority,
          reason: `Not yet active (starts at ${schedule.time})`,
          nextEvaluationTime: this.parseTimeToDate(
            schedule.time,
            context.currentTime
          ),
        };
      }
    }

    // Calculate deactivation time (end of current Hijri day)
    const deactivationTime = new Date(context.currentTime);
    deactivationTime.setHours(23, 59, 59, 999);

    return {
      isActive: true,
      effectivePriority: playlist.priority,
      deactivationTime,
      reason: `Active on ${this.getHijriMonthName(currentMonth)} ${currentDay}${
        schedule.time ? ` at ${schedule.time}` : ""
      } (${hijriDate.year} AH)`,
      nextEvaluationTime: deactivationTime,
    };
  }

  /**
   * Get next state change time
   */
  getNextStateChange(
    playlist: Playlist,
    context: ScheduleContext
  ): Date | null {
    const schedule = playlist.schedule;

    if (
      !schedule ||
      !isHijriDateSchedule(schedule) ||
      !context.prayerTimes?.HijriDateComponents
    ) {
      return null;
    }

    const hijriDate = context.prayerTimes.HijriDateComponents;
    const currentMonth = hijriDate.month;
    const currentDay = hijriDate.day;

    let activeDays: number[];
    try {
      activeDays = parseDaysString(schedule.days);
    } catch {
      return null;
    }

    // If currently in an active date
    if (
      schedule.months.includes(currentMonth) &&
      activeDays.includes(currentDay)
    ) {
      // If time is specified and we haven't reached it yet
      if (schedule.time) {
        const currentTime = this.getTimeString(context.currentTime);
        if (currentTime < schedule.time) {
          return this.parseTimeToDate(schedule.time, context.currentTime);
        }
      }

      // Return end of day
      const endOfDay = new Date(context.currentTime);
      endOfDay.setHours(23, 59, 59, 999);
      return endOfDay;
    }

    // Would need next day's Hijri date to calculate next occurrence
    // For now, return null (will re-evaluate on next prayer times update)
    return null;
  }

  /**
   * Validate configuration
   */
  validateConfiguration(playlist: Playlist): string[] {
    const errors: string[] = [];
    const schedule = playlist.schedule;

    if (!schedule || !isHijriDateSchedule(schedule)) {
      errors.push("HijriDate schedule configuration is missing or invalid");
      return errors;
    }

    const hijriSchedule = schedule as HijriDateSchedule;

    // Validate schedule type
    if (hijriSchedule.type !== ScheduleType.HijriDate) {
      errors.push(
        `Schedule type mismatch: expected ${ScheduleType.HijriDate}, got ${hijriSchedule.type}`
      );
    }

    // Validate months
    if (!schedule.months || schedule.months.length === 0) {
      errors.push("At least one Hijri month must be selected");
    }

    if (schedule.months.some((month) => month < 1 || month > 12)) {
      errors.push(
        "Hijri months must be between 1 (Muharram) and 12 (Dhul Hijjah)"
      );
    }

    // Validate days string
    if (!schedule.days || schedule.days.trim() === "") {
      errors.push("Days configuration is required");
    } else {
      try {
        const days = parseDaysString(schedule.days);
        if (days.length === 0) {
          errors.push("At least one day must be specified");
        }
        if (days.some((day) => day < 1 || day > 30)) {
          errors.push("Hijri days must be between 1 and 30");
        }
      } catch (e) {
        errors.push(`Invalid days format: ${schedule.days}`);
      }
    }

    // Validate time if specified
    if (schedule.time && !this.isValidTimeFormat(schedule.time)) {
      errors.push(`Invalid time format: ${schedule.time}`);
    }

    return errors;
  }

  // ============================================================================
  // HELPER METHODS
  // ============================================================================

  private getTimeString(date: Date): string {
    return date.toTimeString().split(" ")[0]; // "HH:MM:SS"
  }

  private parseTimeToDate(timeStr: string, baseDate: Date): Date {
    const [hours, minutes, seconds] = timeStr.split(":").map(Number);
    const result = new Date(baseDate);
    result.setHours(hours, minutes, seconds || 0, 0);
    return result;
  }

  private isValidTimeFormat(timeStr: string): boolean {
    if (!timeStr) return false;
    const timeRegex = /^([01]\d|2[0-3]):([0-5]\d):([0-5]\d)$/;
    return timeRegex.test(timeStr);
  }

  /**
   * Get the name of a Hijri month
   */
  private getHijriMonthName(month: number): string {
    const months = [
      "Muharram", // 1
      "Safar", // 2
      "Rabi' al-Awwal", // 3
      "Rabi' al-Thani", // 4
      "Jumada al-Awwal", // 5
      "Jumada al-Thani", // 6
      "Rajab", // 7
      "Sha'ban", // 8
      "Ramadan", // 9 - Most important month (fasting)
      "Shawwal", // 10 - Eid al-Fitr (1-3)
      "Dhul-Qa'dah", // 11 - Sacred month
      "Dhul-Hijjah", // 12 - Hajj & Eid al-Adha (10-13)
    ];
    return months[month - 1] || "Unknown";
  }
}
